// [목적] TourAPI 연동 도메인(혼잡도·연관 POI)의 BE/FE 공유 zod 스키마 및 타입 정의
// [이유] tourapi-quiet-and-relation Spec Wave 4 — GET /pois/:id/congestion, /pois/:id/related 응답 계약을
//         packages/types 에 단일 정의함으로써 BE 컨트롤러 검증·FE API 클라이언트·MSW mock 이 동일 스키마 사용
// [사이드 이팩트] apps/api 컨트롤러 + apps/web API 클라이언트 양쪽에서 import. 스키마 변경 시 양쪽 회귀 검증 필수
// [수정 시 고려사항] plan-generate Spec 에서 ragCandidateMetaSchema / dayPlanCoVisitInputSchema 를 import 함.
//                   congestionForecastSchema.points[].rate 는 정수(int)로 강제 — 소수 반환 시 컨트롤러 레벨에서 Math.round 처리
//                   버그 수정(2026-09-17, poi-congestion-related-api Spec): poiId/srcPoiId/dstPoiId 를
//                   z.string().uuid() → z.string().min(1) 로 완화. 실 식별자는 apps/api/src/modules/poi
//                   v2 Silver `poi.content_id`(TourAPI KorService2 contentid, 순수 숫자 문자열, PRIMARY KEY text)이며
//                   uuid 형식이 아님 — 기존 제약대로면 실 데이터 파싱이 전부 실패했음(GET /pois/:id/congestion·related
//                   404/미구현 상태로 은폐돼 있던 drift). ragCandidateMetaSchema/dayPlanCoVisitInputSchema 는
//                   아직 어떤 모듈도 import 하지 않는 미배선 스키마(2026-09-17 기준) — 이번 완화가 회귀를 만들지 않음.

import { z } from 'zod';

// ─── RAG 파이프라인 인터페이스 ─────────────────────────────────────────────────

// [목적] BE 가 RAG 후보 산출 시 각 POI 에 부착하는 메타 — plan-generate Spec 에서 import 강제
// [이유] plan-generate Wave 에서 PoiCongestionService.getConcentrationAvg() 결과를 이 타입으로 정규화해
//         LLM 프롬프트 컨텍스트에 주입. 스키마로 환각값(범위 이탈) 즉시 차단
// [사이드 이팩트] plan-generate Spec(Wave 6 RAG 파이프라인)이 이 타입을 직접 import — 변경 시 해당 Spec PR 동반 갱신
// [수정 시 고려사항] concentrationRateAvg 가 null 이면 RAG hard filter 미적용 처리 — 0(혼잡 없음)과 구분 필수.
//                   범위(0-100)는 DDL concentration_rate 컬럼 COMMENT 와 정합 유지
export const ragCandidateMetaSchema = z.object({
  poiId: z.string().min(1), // poi.content_id (TourAPI contentid, 숫자 문자열) — uuid 아님
  concentrationRateAvg: z.number().min(0).max(100).nullable(),
});
export type RagCandidateMeta = z.infer<typeof ragCandidateMetaSchema>;

// [목적] Day-plan 동선 묶음 함수가 받는 co-visit 입력 — plan-generate Spec 에서 import 강제
// [이유] plan-generate Day-plan 동선 묶음 서비스가 PoiRelatedService.getTopCoVisit() 결과를
//         이 타입으로 정규화해 가중치 합성(의미 유사도 + co-visit score) 에 사용
// [사이드 이팩트] plan-generate Spec(Wave 5 동선 묶음)이 이 타입을 직접 import — 변경 시 해당 Spec PR 동반 갱신
// [수정 시 고려사항] score 범위(0-1)는 poi_related.score NUMERIC(5,4) 컬럼 도메인과 정합.
//                   dst 배열 길이는 컨트롤러 쿼리 cap(n≤50)과 일치 — 상한 변경 시 함께 갱신
export const dayPlanCoVisitInputSchema = z.object({
  srcPoiId: z.string().min(1), // poi.content_id — uuid 아님
  dst: z.array(
    z.object({
      poiId: z.string().min(1), // poi.content_id — uuid 아님
      score: z.number().min(0).max(1),
    }),
  ),
});
export type DayPlanCoVisitInput = z.infer<typeof dayPlanCoVisitInputSchema>;

// ─── REST API 응답 스키마 ──────────────────────────────────────────────────────

// [목적] FE "추천 방문 시간" 카드 응답 스키마 — GET /pois/:id/congestion
// [이유] 30일 예측 집중률 시리즈를 FE 차트/라벨 렌더링에 그대로 소비.
//         zod 파싱으로 BE 응답의 타입 오류(rate 소수, date 형식 이탈)를 FE 진입 전 차단
// [사이드 이팩트] apps/web API 클라이언트가 이 스키마로 응답 파싱 — BE congestionForecastSchema 와 동일 참조
// [수정 시 고려사항] points[].date 는 ISO YYYY-MM-DD 형식 강제.
//                   points[].rate 는 정수(int) 강제 — BE 서비스 레이어에서 Math.round 처리 전제.
//                   빈 points 배열은 "데이터 준비 중" 상태 (404 아님) — FE fallback 처리 필수
//                   poiId 는 poi.content_id(TourAPI contentid) — uuid 아님(2026-09-17 완화, 상단 파일 주석 참조).
export const congestionForecastSchema = z.object({
  poiId: z.string().min(1),
  points: z.array(
    z.object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // ISO date YYYY-MM-DD
      rate: z.number().int().min(0).max(100),
    }),
  ),
});
export type CongestionForecast = z.infer<typeof congestionForecastSchema>;

// [목적] FE 연관 POI 응답 스키마 — GET /pois/:id/related
// [이유] co-visit 기반 연관 POI 목록을 FE "같은 코스를 다르게 즐긴 폴리오" 섹션 및 plan-generate Fork 추천에 제공.
//         score/rank 타입 오류를 FE 진입 전 zod 파싱으로 차단
// [사이드 이팩트] apps/web API 클라이언트가 이 스키마로 응답 파싱.
//                plan-generate Fork 추천 서비스도 이 스키마를 재사용 예정
// [수정 시 고려사항] items[].score 범위(0-1)는 v2 Silver poi_related.rlte_rank(연관 순위, 낮을수록 강한 연관)를
//                   1/rank 로 정규화한 파생값 — TourAPI 원본에 실제 score 필드 없음(v1 uuid 스키마 시절의
//                   T-map 실측 score 컬럼과는 다른 개념). 실제 co-visit 빈도 기반 score 는 후속 Spec 과제.
//                   items[].rank 는 nullable — DB 미적재 행 또는 rank 컬럼 NULL 허용 정책 반영.
//                   빈 items 배열은 "연관 데이터 없음" (404 아님) — FE fallback 처리 필수.
//                   srcPoiId/dstPoiId 는 poi.content_id — uuid 아님(2026-09-17 완화, 상단 파일 주석 참조).
//                   [2026-09-17] OQ-3 확정 — poi_id=src, dst_poi_id=dst 매칭(05_related_poi_match.sql).
//                   RelatedEnrichmentService 가 pg_trgm 임계값으로 채우며, 매칭 전·미달 행은 items 에서 제외된다.
export const poiRelatedListSchema = z.object({
  srcPoiId: z.string().min(1),
  items: z.array(
    z.object({
      dstPoiId: z.string().min(1),
      score: z.number().min(0).max(1),
      rank: z.number().int().min(1).nullable(),
    }),
  ),
});
export type PoiRelatedList = z.infer<typeof poiRelatedListSchema>;
